import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";
import { toast } from "sonner";
import { AdminAlert, AdminEmptyState, AdminPageHeader } from "@/components/shell/AdminPage";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ApiError } from "@/lib/api";
import {
  capturePlatformTemplatePreview,
  capturePlatformTemplatePreviews,
  fetchPlatformTemplates,
  importPlatformTemplate,
  updatePlatformTemplateListed,
  type PlatformTemplateRow,
} from "@/lib/platform-api";

type Filter = "all" | "pending" | "imported" | "preview";

function isImported(row: PlatformTemplateRow) {
  return Boolean(row.imported ?? row.brand_site?.id);
}

function needsPreview(row: PlatformTemplateRow) {
  if (!isImported(row)) return false;
  if (row.needs_preview_capture != null) return row.needs_preview_capture;
  const s = row.preview_status;
  return s === "missing" || s === "stale" || s === "failed";
}

function previewLabel(status?: string) {
  switch (status) {
    case "fresh":
      return "预览最新";
    case "missing":
      return "缺预览图";
    case "stale":
      return "预览过期";
    case "queued":
      return "截图排队";
    case "running":
      return "截图中";
    case "failed":
      return "截图失败";
    default:
      return "—";
  }
}

function previewBadgeVariant(status?: string): "secondary" | "outline" | "destructive" | "default" {
  if (status === "fresh") return "secondary";
  if (status === "failed") return "destructive";
  if (status === "queued" || status === "running") return "default";
  return "outline";
}

export default function PlatformTemplatesPage() {
  const [rows, setRows] = useState<PlatformTemplateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>("all");
  const [importingAll, setImportingAll] = useState(false);
  const [capturingBatch, setCapturingBatch] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);

  const load = useCallback(async (opts?: { quiet?: boolean }) => {
    if (!opts?.quiet) setLoading(true);
    setError(null);
    try {
      const result = await fetchPlatformTemplates();
      setRows(result.templates ?? []);
      setPreviewBusy(Boolean(result.preview_capture?.busy));
    } catch (e) {
      const message = e instanceof ApiError ? e.message : "加载平台模板失败";
      setError(message);
      if (!opts?.quiet) setRows([]);
    } finally {
      if (!opts?.quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  // 有截图任务时短轮询列表（串行队列进度）
  useEffect(() => {
    const capturing = rows.some((r) => r.preview_status === "queued" || r.preview_status === "running");
    if (!capturing && !previewBusy) return;
    const id = window.setInterval(() => {
      void load({ quiet: true });
    }, 3000);
    return () => window.clearInterval(id);
  }, [rows, previewBusy, load]);

  const pendingCount = useMemo(() => rows.filter((r) => !isImported(r)).length, [rows]);
  const importedCount = useMemo(() => rows.filter((r) => isImported(r)).length, [rows]);
  const previewNeedCount = useMemo(() => rows.filter((r) => needsPreview(r)).length, [rows]);

  const visibleRows = useMemo(() => {
    if (filter === "pending") return rows.filter((r) => !isImported(r));
    if (filter === "imported") return rows.filter((r) => isImported(r));
    if (filter === "preview") return rows.filter((r) => needsPreview(r));
    return rows;
  }, [rows, filter]);

  async function onToggleListed(row: PlatformTemplateRow, listed: boolean) {
    setBusyKey(row.source_key);
    try {
      const result = await updatePlatformTemplateListed(row.source_key, listed);
      const updated = result.template;
      setRows((prev) =>
        prev.map((r) => (r.source_key === row.source_key ? { ...r, ...updated } : r)),
      );
      toast.success(listed ? "已上架" : "已下架");
    } catch (e) {
      const message = e instanceof ApiError ? e.message : "更新上架状态失败";
      toast.error(message);
    } finally {
      setBusyKey(null);
    }
  }

  async function onImport(row: PlatformTemplateRow) {
    setBusyKey(row.source_key);
    try {
      const result = await importPlatformTemplate(row.source_key);
      const updated = result.template;
      setRows((prev) =>
        prev.map((r) => (r.source_key === row.source_key ? { ...r, ...updated } : r)),
      );
      toast.success(`已导入「${updated.name || row.name}」`);
    } catch (e) {
      const message = e instanceof ApiError ? e.message : "导入失败";
      toast.error(message);
    } finally {
      setBusyKey(null);
    }
  }

  async function onImportAllPending() {
    const pending = rows.filter((r) => !isImported(r));
    if (pending.length === 0) {
      toast.message("没有待导入模板");
      return;
    }
    setImportingAll(true);
    let ok = 0;
    let fail = 0;
    for (const row of pending) {
      setBusyKey(row.source_key);
      try {
        const result = await importPlatformTemplate(row.source_key);
        const updated = result.template;
        setRows((prev) =>
          prev.map((r) => (r.source_key === row.source_key ? { ...r, ...updated } : r)),
        );
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setBusyKey(null);
    setImportingAll(false);
    if (fail === 0) toast.success(`已导入 ${ok} 套模板`);
    else toast.error(`成功 ${ok} 套，失败 ${fail} 套`);
  }

  async function onCapture(row: PlatformTemplateRow, force = false) {
    setBusyKey(row.source_key);
    try {
      const result = await capturePlatformTemplatePreview(row.source_key, force);
      const updated = result.template;
      setRows((prev) =>
        prev.map((r) => (r.source_key === row.source_key ? { ...r, ...updated } : r)),
      );
      setPreviewBusy(Boolean(result.preview_capture?.busy));
      toast.success("已加入截图队列（全局一次只跑 1 个）");
    } catch (e) {
      const message = e instanceof ApiError ? e.message : "入队截图失败";
      toast.error(message);
    } finally {
      setBusyKey(null);
    }
  }

  async function onCaptureAllNeeding() {
    const keys = rows.filter((r) => needsPreview(r)).map((r) => r.source_key);
    if (keys.length === 0) {
      toast.message("没有需要更新的预览图");
      return;
    }
    setCapturingBatch(true);
    try {
      const result = await capturePlatformTemplatePreviews({ source_keys: keys });
      setPreviewBusy(Boolean(result.preview_capture?.busy) || result.enqueued.length > 0);
      toast.success(`已入队 ${result.enqueued.length} 个截图任务（串行执行）`);
      await load({ quiet: true });
    } catch (e) {
      const message = e instanceof ApiError ? e.message : "批量入队失败";
      toast.error(message);
    } finally {
      setCapturingBatch(false);
    }
  }

  return (
    <div className="w-full space-y-6">
      <AdminPageHeader
        title="平台模板"
        description="扫描种子：未导入可一键挂站；预览图缺失/过期可一键截图（全局串行，一次只跑 1 个）。"
      />
      {error ? <AdminAlert>{error}</AdminAlert> : null}
      {previewBusy ? (
        <AdminAlert tone="info">
          预览截图正在排队执行（同时只跑 1 个），列表会自动刷新，无需重复点击。
        </AdminAlert>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={loading} onClick={() => void load()}>
          重新扫描
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={loading || importingAll || pendingCount === 0}
          onClick={() => void onImportAllPending()}
        >
          {importingAll ? "导入中…" : `一键导入全部未导入（${pendingCount}）`}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          disabled={loading || capturingBatch || previewNeedCount === 0}
          onClick={() => void onCaptureAllNeeding()}
        >
          {capturingBatch ? "入队中…" : `一键更新预览图（${previewNeedCount}）`}
        </Button>
        <div className="ml-auto flex flex-wrap gap-1">
          {(
            [
              ["all", `全部 ${rows.length}`],
              ["pending", `未导入 ${pendingCount}`],
              ["imported", `已导入 ${importedCount}`],
              ["preview", `需更新预览 ${previewNeedCount}`],
            ] as const
          ).map(([key, label]) => (
            <Button
              key={key}
              type="button"
              size="sm"
              variant={filter === key ? "default" : "ghost"}
              onClick={() => setFilter(key)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>名称</TableHead>
              <TableHead>分类</TableHead>
              <TableHead className="w-[90px]">导入</TableHead>
              <TableHead className="w-[110px]">预览图</TableHead>
              <TableHead>slug</TableHead>
              <TableHead className="w-[100px]">上架</TableHead>
              <TableHead>正式 URL</TableHead>
              <TableHead className="w-[220px]">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="h-24 text-center text-muted-foreground">
                  扫描中…
                </TableCell>
              </TableRow>
            ) : visibleRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="p-0">
                  <AdminEmptyState>
                    <p className="font-medium text-foreground">
                      {rows.length === 0 ? "暂无平台模板" : "当前筛选下没有模板"}
                    </p>
                    <p className="mt-2">
                      {rows.length === 0
                        ? "请确认 brand-site 已种子化 WebsiteTemplate，且 PLATFORM_BRAND_SITE_ACCOUNT_ID 已配置。"
                        : "切换上方筛选或重新扫描。"}
                    </p>
                  </AdminEmptyState>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map((row) => {
                const imported = isImported(row);
                const slug = row.brand_site?.slug || row.source_key;
                const busy = busyKey === row.source_key || importingAll || capturingBatch;
                const capturing =
                  row.preview_status === "queued" || row.preview_status === "running";
                const idePath = `/platform/templates/${encodeURIComponent(row.source_key)}/ide`;
                return (
                  <TableRow key={row.source_key}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {row.category?.name || row.category?.slug || "—"}
                    </TableCell>
                    <TableCell>
                      {imported ? (
                        <Badge variant="secondary">已导入</Badge>
                      ) : (
                        <Badge variant="outline">未导入</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <Badge variant={previewBadgeVariant(row.preview_status)}>
                          {previewLabel(row.preview_status)}
                        </Badge>
                        {row.preview_capture_error ? (
                          <p className="max-w-[140px] truncate text-xs text-destructive" title={row.preview_capture_error}>
                            {row.preview_capture_error}
                          </p>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm text-muted-foreground">{slug}</TableCell>
                    <TableCell>
                      <Switch
                        size="sm"
                        checked={row.listed}
                        disabled={busy || !imported}
                        aria-label={`${row.name} 上架`}
                        onCheckedChange={(checked) => void onToggleListed(row, checked)}
                      />
                    </TableCell>
                    <TableCell>
                      {row.public_url ? (
                        <a
                          href={row.public_url}
                          target="_blank"
                          rel="noreferrer"
                          className="text-sm text-primary underline-offset-4 hover:underline"
                        >
                          {row.public_url}
                        </a>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2">
                        {imported ? (
                          <>
                            <Button type="button" variant="outline" size="sm" asChild>
                              <Link to={idePath}>打开 IDE</Link>
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="secondary"
                              disabled={busy || capturing}
                              onClick={() => void onCapture(row, !needsPreview(row))}
                            >
                              {capturing ? "截图中…" : needsPreview(row) ? "更新预览" : "重截预览"}
                            </Button>
                          </>
                        ) : (
                          <Button
                            type="button"
                            size="sm"
                            disabled={busy}
                            onClick={() => void onImport(row)}
                          >
                            {busyKey === row.source_key ? "导入中…" : "一键导入"}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
